import { formatCalendarDateForInput, getLocalCalendarDate } from "../Utils/calendarDate.mjs";

function dateKey(entry) {
  const value = entry?.treatment_date?.date;
  return value instanceof Date && !Number.isNaN(value.getTime())
    ? getLocalCalendarDate(value)
    : formatCalendarDateForInput(value);
}

const textOrder = new Intl.Collator("en", { numeric: true, sensitivity: "base" });

function sortValue(entry, column) {
  if (column === "date") return dateKey(entry);
  const treatment = entry?.treatment_date;
  if (treatment?.id === null) return null;
  switch (column) {
    case "provider":
      return (entry.group?.case_provider || entry.client_provider)?.treatment_location?.name;
    case "cpt_codes":
      return [treatment?.cpt_codes, treatment?.procedure_code_modifiers]
        .filter(Boolean)
        .join(" - ");
    case "diagnosis_codes":
    case "body_part":
      return treatment?.[column];
    case "description":
      return entry?.date_type === "tf_date" ? "" : treatment?.description;
    case "fees": {
      const value = treatment?.fees;
      if (value === null || value === undefined || String(value).trim() === "") return null;
      const amount = Number(String(value).replace(/[$,]/g, ""));
      return Number.isFinite(amount) ? amount : null;
    }
    default:
      return null;
  }
}

function sortEntries(entries, direction, column) {
  // Gap markers belong to the interval before the following treatment date.
  const blocks = [];
  let gaps = [];
  for (const entry of entries) {
    if (entry?.gap) {
      // Treatment gaps only describe neighboring rows when ordered by date.
      if (column !== "date") continue;
      gaps.push(entry);
    } else {
      blocks.push({ entry, gaps, key: sortValue(entry, column) });
      gaps = [];
    }
  }
  blocks.sort((a, b) => {
    const missing = (value) => value === null || value === undefined || String(value).trim() === "";
    if (missing(a.key)) return missing(b.key) ? 0 : 1;
    if (missing(b.key)) return -1;
    const order =
      column === "fees" ? a.key - b.key : textOrder.compare(String(a.key), String(b.key));
    return order * (direction === "desc" ? -1 : 1);
  });
  return [
    ...blocks.flatMap(({ entry, gaps: before }) =>
      direction === "desc" ? [entry, ...before] : [...before, entry]
    ),
    ...gaps,
  ];
}

export function sortChronology(data = [], expanded = false, direction = "asc", column = "date") {
  if (!direction) return data;
  if (!expanded) return sortEntries(data, direction, column);

  const entries = data.flatMap((group) =>
    (group?.dates || []).map((entry) => ({ ...entry, group }))
  );
  // Rebuild consecutive provider sections after sorting across all providers.
  return sortEntries(entries, direction, column).reduce((groups, { group, ...entry }) => {
    let section = groups.at(-1);
    if (!section || section.case_provider !== group.case_provider) {
      section = { ...group, dates: [] };
      groups.push(section);
    }
    section.dates.push({ ...entry, show_date: undefined });
    return groups;
  }, []);
}
